import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Util } from 'src/app/util';
import { HistoryItem, HistoryItemType } from '../../../models/history-user';
import { CustomerType } from '../../../models/client-configuration';
import { ConfigService } from '../../../services/config.service';
import { ButtonsConfiguration, HistoryButtonConfiguration } from '../../../models/buttons-config';
import { BaseData } from '../../card-item/models/base-data.model';
import { DetailsCardItem } from '../../card-item/models/details-card-item.model';
import { TranslateService } from '@ngx-translate/core';
import { DatePipe } from '@angular/common';
import { TranslateByCustomerTypePipe } from 'src/app/pipes/translate-by-customer-type.pipe';
import { UserHistoryService } from '../user-history/service/user-history.service';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { Router } from '@angular/router';

@Component({
  selector: 'app-user-history-item',
  templateUrl: './user-history-item.component.html',
  styleUrls: ['./user-history-item.component.scss'],
  providers: [TranslateByCustomerTypePipe],
})
export class UserHistoryItemComponent implements OnInit, OnDestroy {
  @Input()
  historyItem: HistoryItem;

  @Input()
  corporateOrAcademicCustomer: boolean;

  @Output()
  removed = new EventEmitter<HistoryItem>();

  slug: string;
  endDateKey: string;
  showRecordButtons: boolean;
  showStartDate: boolean;
  shouldStack: boolean;
  customerType: CustomerType;
  config: ButtonsConfiguration;

  baseData: BaseData;
  detailsLeft: DetailsCardItem[] = [];
  detailsRight: DetailsCardItem[] = [];

  private unsubscribe: Subject<void> = new Subject();

  constructor(
    configService: ConfigService,
    private translate: TranslateService,
    private datePipe: DatePipe,
    private translateByCustomerType: TranslateByCustomerTypePipe,
    private userHistoryService: UserHistoryService,
    private router: Router
  ) {
    this.customerType = configService.customerType;
    this.config = new HistoryButtonConfiguration();
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  ngOnInit() {
    const isLearningExperience = Boolean(this.historyItem.record.learningExperienceId);
    this.slug = Util.slugify(this.historyItem.record.title + '-' + this.historyItem.recordId);
    this.shouldStack = ['MAGAZINE', 'NEWSPAPER'].includes(this.historyItem.record.specialFormat);
    this.showRecordButtons = this.historyItem.type !== HistoryItemType.VISUALIZATION;
    this.showStartDate = this.historyItem.start && this.historyItem.type === HistoryItemType.PRESTAMO_HISTORICO;
    this.initializeEndDateKey();
    this.fillDetailsLeft(isLearningExperience);
    this.fillDetailsRight(isLearningExperience);
    this.buildBaseData();
  }

  initializeEndDateKey() {
    if (this.corporateOrAcademicCustomer || this.historyItem.type === HistoryItemType.PRESTAMO_HISTORICO || this.historyItem.type === HistoryItemType.PHYSICAL_CHECKOUT) {
      this.endDateKey = 'user.history.checkout.end';
    } else {
      this.endDateKey = 'user.history.visualization.date';
    }
  }

  openItemDetail() {
    this.router.navigate(['/info', this.slug]);
  }

  onRemoved(item: HistoryItem) {
    this.removed.emit(item);
  }

  private fillDetailsLeft(isLearningExperience: boolean) {
    if (this.historyItem.type === HistoryItemType.PHYSICAL_CHECKOUT) {
      const checkoutDateLabel = this.translateByCustomerType.transform(
        'user.history.checkout.start',
        this.customerType.dbValue
      );
      this.detailsLeft.push({
        label: this.translate.instant(checkoutDateLabel),
        data: this.translate.instant(this.datePipe.transform(this.historyItem.start)),
      });

      return;
    }

    if (this.historyItem.end && !isLearningExperience) {
      if (this.corporateOrAcademicCustomer) {
        const lastAccessLabel = this.translateByCustomerType.transform(this.endDateKey, this.customerType.dbValue);
        this.detailsLeft.push({
          label: this.translate.instant(lastAccessLabel),
          data: this.translate.instant(this.datePipe.transform(this.historyItem.end)),
        });
      }

      if (!this.corporateOrAcademicCustomer && this.historyItem.type !== HistoryItemType.PRESTAMO_HISTORICO) {
        const visualizationDateLabel = this.translateByCustomerType.transform(
          this.endDateKey,
          this.customerType.dbValue
        );
        this.detailsLeft.push({
          label: this.translate.instant(visualizationDateLabel),
          data: this.translate.instant(this.datePipe.transform(this.historyItem.end)),
        });
      }
    }

    if (this.showStartDate && !this.corporateOrAcademicCustomer && !isLearningExperience) {
      const checkoutDateLabel = this.translateByCustomerType.transform(
        'user.history.checkout.start',
        this.customerType.dbValue
      );
      this.detailsLeft.push({
        label: this.translate.instant(checkoutDateLabel),
        data: this.translate.instant(this.datePipe.transform(this.historyItem.start)),
      });
    }

    if (this.historyItem.end && isLearningExperience) {
      const lastAccessLabel = this.translateByCustomerType.transform(
        'user.history.checkout.end.academic',
        this.customerType.dbValue
      );
      this.detailsLeft.push({
        label: this.translate.instant(lastAccessLabel),
        data: this.translate.instant(this.datePipe.transform(this.historyItem.end)),
      });
    }
  }

  private fillDetailsRight(isLearningExperience: boolean) {
    if (this.historyItem.type === HistoryItemType.PHYSICAL_CHECKOUT) {
      const visualizationDateLabel = this.translateByCustomerType.transform(
        this.endDateKey,
        this.customerType.dbValue
      );
      this.detailsRight.push({
        label: this.translate.instant(visualizationDateLabel),
        data: this.translate.instant(this.datePipe.transform(this.historyItem.end)),
      });

      return;
    }

    this.userHistoryService.loadMoreItems$
      .pipe(
        filter((res) => res),
        takeUntil(this.unsubscribe)
      )
      .subscribe(() => {
        this.detailsRight = [];
        if (this.historyItem.end && this.corporateOrAcademicCustomer && this.historyItem.ebookTimeReading) {
          this.detailsRight.push({
            label: this.translate.instant('user.history.checkout.consumptiontime'),
            data: this.historyItem.ebookTimeReading,
          });
        }
        if (
          this.historyItem.end &&
          !this.corporateOrAcademicCustomer &&
          this.historyItem.type === HistoryItemType.PRESTAMO_HISTORICO &&
          !isLearningExperience
        ) {
          const visualizationDateLabel = this.translateByCustomerType.transform(
            this.endDateKey,
            this.customerType.dbValue
          );
          this.detailsRight.push({
            label: this.translate.instant(visualizationDateLabel),
            data: this.translate.instant(this.datePipe.transform(this.historyItem.end)),
          });
        }
      });
  }

  private buildBaseData() {
    this.baseData = {
      record: this.historyItem.record,
      author: this.historyItem.record.author,
      title: this.historyItem.record.title,
      cover: this.historyItem.record.coverImageUrl,
      icon: this.historyItem.record.icon,
    };
  }
}
